// A Properties project continuing an illustrative project whose calculations need a changed model (e.g. graphene
// with Rashba and exchange): the changes, applied with one click by a properties.modify job (as "Modify model" does).
import { Alert, Button, Group, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { ErrorBox } from "../../components/Errors";
import { JobProgress } from "../../components/Jobs";
import { post, type Job, type ProjectDetail } from "../../lib/api";
import { describeModifiers } from "../../lib/fields";
import { useLiveJob } from "../../lib/hooks";
import type { ModelSummary } from "../../lib/negf";
import type { PropertiesMemory } from "../../lib/properties";

/** True when the model already carries every change of the preset (same kinds, in order). */
export function presetApplied(preset: Record<string, unknown>[], applied: Record<string, any>[] | undefined): boolean {
  const kinds = (applied ?? []).filter((m) => !(m.kind === "spinful" && m.auto)).map((m) => m.kind);
  const want = preset.map((m) => m.kind);
  return want.length > 0 && want.every((k, i) => kinds[i] === k);
}

export function PresetModifiers({ project, mem, remember }: {
  project: ProjectDetail; mem: PropertiesMemory; remember: (m: Partial<PropertiesMemory>) => void;
}) {
  const qc = useQueryClient();
  const mods = mem.preset?.modifiers ?? [];
  const { job } = useLiveJob(mem.modify_job);
  useEffect(() => {
    if (job?.status === "succeeded" && job.id === mem.modify_job && job.result?.model_file && job.result.model_file !== mem.model_file)
      remember({ model_file: job.result.model_file as string, summary: job.result.summary as ModelSummary, modify_job: undefined });
  }, [job, mem.modify_job, mem.model_file, remember]);
  const apply = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: "properties.modify", project_id: project.id,
      params: { model_file: mem.base_file ?? mem.model_file, modifiers: mods } }),
    onSuccess: (j) => {
      remember({ modify_job: j.id, base_file: mem.base_file ?? mem.model_file, base_summary: mem.base_summary ?? mem.summary });
      qc.invalidateQueries({ queryKey: ["project", project.id] });
    },
  });
  if (!mods.length || !mem.model_file || presetApplied(mods, mem.summary?.modifiers)) return null;
  const busy = job && (job.status === "queued" || job.status === "running");
  return (
    <Alert color="grape" p="xs" mb="sm" data-testid="preset-modifiers">
      <Group justify="space-between" wrap="nowrap">
        <Text size="sm"><b>{mem.preset!.name}</b> continues with these changes to the model: {describeModifiers(mods).join(" · ")}.
          Apply them first (Modify model shows them and resets them).</Text>
        <Button size="xs" onClick={() => apply.mutate()} loading={apply.isPending || !!busy} data-testid="apply-preset-modifiers">
          Apply these changes</Button>
      </Group>
      {busy && <JobProgress job={job!} />}
      <ErrorBox error={apply.error ?? (job?.error ? new Error(job.error) : null)} />
    </Alert>
  );
}
